import { Marquee } from "./Marquee";
import { PartnerBadge } from "./PartnerBadge";
import { CoFounders } from "./CoFounders";

const HERO_LOGO =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_fa35d69f-5986-4533-b1b4-846f1468831d/artifacts/u3cuk9ea_OUTDOORS%20REIMAGINED%20LOGO.jpg";

// The manifesto, kept verbatim, split into rhythmic blocks for the reveal.
const MANIFESTO = [
  "At Outdoors Reimagined\u2122, we believe the outdoors should be more accessible, more rewarding, and easier to experience\u2014without losing the traditions that make it worth protecting.",
  "We build and support innovative outdoor brands that connect people with places, experiences, equipment, and opportunities that might otherwise remain out of reach.",
  "From helping outdoor property owners share what they have built to helping families, sportsmen, and adventurers create memories they will carry for a lifetime, everything we do is rooted in respect for the land and the people who live for it.",
  "We are not here to change what the outdoors means. We are here to create better ways for people to experience it, share it, and help it thrive for generations to come.",
];

const HEADLINE_WORDS = ["The", "Outdoors,", "Reimagined."];

export default function Landing() {
  return (
    <main className="App relative w-full bg-[#0f1c16]" data-testid="landing-root">
      <section
        className="grain relative flex min-h-screen w-full flex-col overflow-hidden"
        data-testid="hero-section"
      >
        {/* Full-page hero logo image with a slow on-load zoom */}
        <div className="hero-zoom absolute inset-0 z-0">
          <img
            src={HERO_LOGO}
            alt="Outdoors Reimagined Holdings"
            data-testid="hero-logo-image"
            className="h-full w-full object-cover object-center"
          />
        </div>

        {/* Cinematic overlays for depth + legibility */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[1] bg-[#0a130e]/60"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[2] bg-gradient-to-b from-[#0a130e]/70 via-transparent to-[#0a130e]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[2] bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(10,19,14,0.85)_100%)]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[2] bg-gradient-to-r from-[#0a130e]/85 via-[#0a130e]/35 to-transparent"
        />

        {/* Top bar */}
        <header className="relative z-20 flex items-center justify-between px-6 py-6 md:px-12 md:py-8">
          <div
            className="reveal flex items-center gap-3"
            style={{ animationDelay: "0.25s" }}
          >
            <span className="h-px w-8 bg-[#c6a664]/70 md:w-12" />
            <span className="font-body text-[10px] uppercase tracking-[0.32em] text-[#f4f1ed]/90 md:text-xs">
              Outdoors Reimagined&trade; Holdings
            </span>
          </div>
          <span
            className="reveal hidden font-body text-[10px] uppercase tracking-[0.32em] text-[#a3b1a9] sm:block md:text-xs"
            style={{ animationDelay: "0.35s" }}
          >
            outdoors-reimagined.com
          </span>
        </header>

        {/* Center content */}
        <div className="relative z-20 flex flex-1 items-center px-6 py-10 md:px-12">
          <div
            data-testid="manifesto-box"
            className="w-full max-w-2xl [text-shadow:0_2px_20px_rgba(10,19,14,0.9)]"
          >
            {/* Eyebrow */}
            <div
              className="reveal mb-4 flex items-center gap-3"
              style={{ animationDelay: "0.5s" }}
            >
              <span className="h-1.5 w-1.5 rotate-45 bg-[#c6a664]" />
              <span className="font-body text-[10px] uppercase tracking-[0.4em] text-[#c6a664] md:text-xs">
                Our Ethos
              </span>
            </div>

            {/* Masked headline */}
            <h1
              data-testid="hero-heading"
              aria-label="The Outdoors, Reimagined."
              className="font-display text-5xl font-bold leading-[1] tracking-tight text-white sm:text-6xl md:text-7xl"
            >
              {HEADLINE_WORDS.map((word, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className="mr-3 inline-block overflow-hidden py-2"
                >
                  <span
                    className={`reveal-mask ${
                      i === HEADLINE_WORDS.length - 1
                        ? "italic text-[#c6a664]"
                        : ""
                    }`}
                    style={{ animationDelay: `${0.62 + i * 0.12}s` }}
                  >
                    {word}
                    {i === HEADLINE_WORDS.length - 1 && (
                      <sup className="ml-1 align-super text-[0.3em] not-italic tracking-normal text-[#c6a664]">
                        &trade;
                      </sup>
                    )}
                  </span>
                </span>
              ))}
            </h1>

            <div
              className="reveal-line my-6 h-px w-full bg-gradient-to-r from-[#c6a664]/60 to-transparent"
              style={{ animationDelay: "1.05s" }}
            />

            {/* Manifesto body */}
            <div className="space-y-4">
              {MANIFESTO.map((para, i) => (
                <p
                  key={i}
                  className="reveal font-display text-base font-normal leading-relaxed text-white md:text-xl"
                  style={{ animationDelay: `${1.2 + i * 0.14}s` }}
                >
                  {para}
                </p>
              ))}

              <p
                data-testid="hero-closing-line"
                className="reveal pt-2 font-display text-lg italic leading-snug text-[#c6a664] md:text-2xl"
                style={{ animationDelay: `${1.2 + MANIFESTO.length * 0.14}s` }}
              >
                Built by outdoorsmen. Driven by possibility. Welcome to the
                future of outdoor recreation.
              </p>
            </div>
          </div>
        </div>

        {/* Editorial marquee near the bottom */}
        <div className="relative z-20 mb-24 md:mb-28">
          <Marquee />
        </div>

        {/* Clickable Shanty BB partner badge, anchored bottom-left of the hero */}
        <PartnerBadge />
      </section>

      {/* Co-Founders */}
      <CoFounders />

      {/* Footer */}
      <footer
        data-testid="site-footer"
        className="relative z-10 w-full border-t border-[#c6a664]/15 bg-[#0a130e] px-6 py-10 md:px-12"
      >
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <span className="font-body text-[10px] uppercase tracking-[0.32em] text-[#a3b1a9] md:text-xs">
            Outdoors Reimagined&trade; Holdings LLC
          </span>
          <span className="font-body text-[10px] uppercase tracking-[0.32em] text-[#a3b1a9]/70 md:text-xs">
            Outdoors Reimagined&trade; — outdoors-reimagined.com
          </span>
        </div>
      </footer>
    </main>
  );
}
